import qs from 'qs';
import { AxiosRequestConfig } from 'axios';
import { httpClient, qsOptions } from 'providers/clients/httpClient';
import { hasValue } from 'utility/functions';
import {
  TQueryField,
  TSortField,
  QueryRule,
  SortRule,
  ResultRule,
  BlockAssetSearchItem,
} from './BlockLibrary';
import type {
  AnalysisRequest,
  AnalysisResponse,
  BinaryUploadParams,
  DeleteEditorialAssetIdLockParams,
  DeleteEditorialAssetIdLockResult,
  PreviewRequest,
  GetEditorialAssetIdLockParams,
  GetEditorialAssetIdLockResult,
  GetEditorialConfigResult,
  KeywordSuggestion,
  PutEditorialAssetIdLockParams,
  PutEditorialAssetIdLockResult,
  PostEditorialAssetDeleteParams,
  PostEditorialAssetDeleteResult,
  DeleteEditorialAssetIdParams,
  DeleteEditorialAssetIdResult,
  PostEditorialAssetDuplicateResult,
  PostEditorialAssetDuplicateFullParams,
  GetBlackbirdLinkResult,
  BinaryUploadResponse,
  BinaryUploadViaAssetIdResponse,
  BinaryUploadViaAssetIdParams,
  BinaryConvertArgs,
  BinaryConvertResponse,
  TPostTrimmedVideoResponse,
  TPostTrimmedVideo,
  EditorialAssetSearchResultItem,
  GetAssetRevisionHistoryResult,
  GetRevisionsContentDiffResult,
  GetAssetRevisionByIdResult,
  GetAssetRevisionHistoryParams,
  FullEditorialAsset,
  GetRevisionsContentDiffParams,
  GetAssetRevisionByIdParams,
  PatchType,
  TAbortCtrlSignal,
  RestoreEditorialAssetByIdParams,
  RestoreEditorialAssetByIdResult,
  PurgeEditorialAssetByIdParams,
  PurgeEditorialAssetByIdResult,
} from 'utility/types/dataProvider';

// API origins for asset locking
export const NXT_LOCK_ORIGIN = 'cosmos-admin';
export const LEGACY_LOCK_ORIGIN = 'tncms-admin';

export type GetEditorialAssetByIdArgs = {
  assetId?: string;
  preview?: PreviewRequest;
};

export type GetEditorialAssetByIdResult = {
  status: string | number;
  data: FullEditorialAsset;
};

export type PatchEditorialAssetArgs = {
  assetId?: string;
  patchList?: PatchType[];
};

export type PatchEditorialAssetResult = {
  status: string | number;
  data: FullEditorialAsset;
};

/**
 * Dynamic collection config
 * GET /editorial/config/collection/dynamic/
 */

/**
 * similar to /core/block/config/ returns
 * eg 'CoreBlockAppConfig' but trimmed down
 * to relevant query/sort fields
 */
type DynamicCollectionConfigData = {
  query_fields: {
    [key: string]: TQueryField;
  };
  sort_fields: TSortField[];
};

export type GetDynamicCollectionConfigResult = {
  status: string | number;
  data: DynamicCollectionConfigData;
};

type PinnedItem = {
  uuid: string;
};

/**
 * Fetch search results for dynamic collection
 * POST /editorial/asset/search/collection/dynamic/
 */
export type GetDynamicCollectionSearchArgs = {
  signal?: TAbortCtrlSignal;
  body?: {
    preview?: PreviewRequest;
    search_rules?: QueryRule[];
    sort_rules?: SortRule[];
    results?: ResultRule;
    pinned?: PinnedItem[];
  };
};

export type GetDynamicCollectionSearchResult = {
  status?: string | number;
  data?: {
    items?: BlockAssetSearchItem[];
    next?: string | null;
    prev?: string | null;
    total?: number;
  };
};

/**
 * -----------------------------------------------
 * Editorial API Provider
 * -----------------------------------------------
 */
export const editorialProvider = {
  binaryUpload: async (params: BinaryUploadParams): Promise<BinaryUploadResponse> => {
    try {
      const file = params?.file;
      const preview = params?.preview;

      if (!file) {
        throw new Error('File is required.');
      }

      const formData = new FormData();

      if (file) {
        formData.append('file', file);
      }

      if (preview) {
        formData.append('preview', JSON.stringify(preview));
      }

      const { status, data } = await httpClient({
        url: '/core/upload/',
        method: 'POST',
        data: formData,
        onUploadProgress: params?.onUploadProgress,
      });

      const response: BinaryUploadResponse = {
        status: data?.status ?? status,
        data: data?.data,
      };

      return response;
    } catch (error) {
      console.error('[binaryUpload] Error:', error);
      throw error;
    }
  },

  // Uplaod binary via existing asset UUID
  binaryUploadViaAssetId: async (
    params: BinaryUploadViaAssetIdParams,
  ): Promise<BinaryUploadViaAssetIdResponse> => {
    try {
      const assetId = params?.uuid;
      const slot = params?.slot;
      const app = params?.app;
      const previewReq = params?.preview;

      if (!assetId || !slot || !app) {
        throw new Error('Missing required parameters.');
      }

      const { status, data } = await httpClient({
        url: '/core/upload/asset/',
        method: 'POST',
        data: {
          app: app,
          asset: assetId,
          slot: slot,
          preview: previewReq,
        },
      });

      const response: BinaryUploadViaAssetIdResponse = {
        status: data?.status ?? status,
        data: data?.data,
      };

      return response;
    } catch (error) {
      console.error('[binaryUploadViaAssetId] Error:', error);
      throw error;
    }
  },

  binaryConvert: async (params: BinaryConvertArgs): Promise<BinaryConvertResponse> => {
    try {
      const fileId = params?.file; // binary id
      const patchList = params?.patch;
      const preview = params?.preview;

      if (!fileId) {
        throw new Error('File binary ID is required.');
      }

      const { status, data } = await httpClient({
        url: '/editorial/asset/binary/',
        method: 'POST',
        data: {
          file: fileId,
          patch: patchList,
          preview: preview,
        },
      });

      const response: BinaryConvertResponse = {
        status: data?.status ?? status,
        data: data?.data,
      };

      return response;
    } catch (error) {
      console.error('[binaryConvert] Error:', error);
      throw error;
    }
  },

  // Get editorial config
  getEditorialConfig: async (): Promise<GetEditorialConfigResult> => {
    try {
      const path = `/editorial/config/`;

      const { data } = await httpClient({
        url: path,
        method: 'GET',
      });

      const editorialConfig: GetEditorialConfigResult = data;
      return editorialConfig;
    } catch (error) {
      console.error('[getEditorialConfig] Error:', error);
      throw error;
    }
  },

  // Editorial semantic analysis
  semanticAnalysis: async ({
    request,
  }: {
    request: AnalysisRequest;
  }): Promise<AnalysisResponse> => {
    try {
      const path = `/editorial/semantic/analyze/`;

      const { data } = await httpClient({
        url: path,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(request),
      });

      const responseData = data;
      const keywords: KeywordSuggestion[] = responseData.data.keywords.map(
        (keywordData: any) => ({
          tag: keywordData.tag,
          score: keywordData.score,
        }),
      );
      const response: AnalysisResponse = {
        status: 'success',
        data: {
          keywords: keywords,
          categories: responseData.data.categories || [],
          iptc: responseData.data.iptc || [],
          iab: responseData.data.iab || [],
        },
      };
      return response;
    } catch (error) {
      console.error('[semanticAnalysis] Error:', error);
      throw error;
    }
  },

  /**
   * Base editorial asset search function
   * Non-RA "getList" version for more direct programmatic usage
   * GET /editorial/asset/search/
   */
  getEditorialAssetSearch: async (
    params: BaseEditorialSearchArgs,
  ): Promise<BaseEditorialSearchResponse> => {
    try {
      const abortSignal = params?.signal;
      const qp = params?.qp || {};
      const payload: Partial<BaseEditorialSearchParams> = {};

      // Build base payload via provided parameters
      if (qp?.app) {
        payload.app = qp.app;
      }
      if (qp?.author) {
        payload.author = qp.author;
      }
      if (qp?.draft) {
        payload.draft = qp.draft;
      }
      if (qp?.flag) {
        payload.flag = qp.flag;
      }
      if (qp?.publish_status) {
        payload.publish_status = qp.publish_status;
      }
      if (qp?.print_ready) {
        payload.print_ready = qp.print_ready;
      }
      if (qp?.print_slug) {
        payload.print_slug = qp.print_slug;
      }
      if (qp?.print_publication) {
        payload.print_publication = qp.print_publication;
      }
      if (qp?.print_publication_status) {
        payload.print_publication_status = qp.print_publication_status;
      }
      if (qp?.photo_sales) {
        payload.photo_sales = qp.photo_sales;
      }
      if (qp?.query) {
        payload.query = qp.query;
      }
      if (qp?.subtype) {
        payload.subtype = qp.subtype;
      }
      if (qp?.section) {
        payload.section = qp.section;
      }
      if (qp?.type) {
        payload.type = qp.type;
      }
      if (qp?.template) {
        payload.template = qp.template;
      }
      if (qp?.uuid) {
        payload.uuid = qp.uuid;
      }
      if (qp?.workflow) {
        payload.workflow = qp.workflow;
      }
      if (qp?.workflow_process) {
        payload.workflow_process = qp.workflow_process;
      }
      // Previews object request
      if (qp?.preview) {
        payload.preview = qp.preview;
      }
      // Booleans (can be explicitly false)
      if (hasValue(qp?.deleted)) {
        payload.deleted = qp.deleted;
      }
      if (hasValue(qp?.contributed)) {
        payload.contributed = qp.contributed;
      }
      if (hasValue(qp?.advertorial)) {
        payload.advertorial = qp.advertorial;
      }
      // Deeper objects - check depth before assignment
      if (qp?.start_time?.min) {
        payload.start_time ??= {};
        payload.start_time.min = qp.start_time.min;
      }
      if (qp?.start_time?.max) {
        payload.start_time ??= {};
        payload.start_time.max = qp.start_time.max;
      }
      if (qp?.print_depth?.min) {
        payload.print_depth ??= {};
        payload.print_depth.min = qp.print_depth.min;
      }
      if (qp?.print_depth?.max) {
        payload.print_depth ??= {};
        payload.print_depth.max = qp.print_depth.max;
      }
      if (qp?.print_publication_date?.min) {
        payload.print_publication_date ??= {};
        payload.print_publication_date.min = qp.print_publication_date.min;
      }
      if (qp?.print_publication_date?.max) {
        payload.print_publication_date ??= {};
        payload.print_publication_date.max = qp.print_publication_date.max;
      }
      // Experimental
      if (qp?.semantic) {
        payload.semantic = qp.semantic;
      }
      if (qp?.semantic_nearest) {
        payload.semantic_nearest = qp.semantic_nearest;
      }
      if (qp?.semantic_candidates) {
        payload.semantic_candidates = qp.semantic_candidates;
      }
      // Pagination and sorting parameters
      if (qp?.sort) {
        payload.sort = qp.sort;
      }
      if (qp?.dir) {
        payload.dir = qp.dir;
      }
      if (qp?.limit) {
        payload.limit = qp.limit;
      }
      if (qp?.page) {
        payload.page = qp.page;
      }

      const baseUrl = `/editorial/asset/search/`;

      const hasPayload = Object.keys(payload).length > 0;

      // the qs library can auto flatten objects to bracket
      // eg: { previews: { size1:'550w' } } -> 'previews[size1]': '550w'
      const qpStr = hasPayload ? qs.stringify(payload, qsOptions) : '';

      // Build query string and final path
      const finalUrl = qpStr ? `${baseUrl}?${qpStr}` : baseUrl;

      const { data, status } = await httpClient<BaseEditorialSearchResponse>({
        url: finalUrl,
        method: 'GET',
        signal: abortSignal,
      });

      // Map uuid to id for react-admin bc compat... however in
      // almost all cases, use the real uuid property instead.
      const baseItems = data?.data?.items ?? [];
      const itemsWithId = baseItems.map((item) => ({
        ...item,
        id: item?.uuid as string,
      }));

      const result: BaseEditorialSearchResponse = {
        status: data?.status ?? status,
        data: {
          ...data?.data,
          items: itemsWithId,
        },
      };

      return result;
    } catch (error) {
      console.error('[getEditorialAssetSearch]:', error);
      throw error;
    }
  },

  // -------------------------------
  // Revisions.
  // -------------------------------

  // Revision history list.
  getAssetRevisionHistory: async (
    params: GetAssetRevisionHistoryParams,
  ): Promise<GetAssetRevisionHistoryResult> => {
    try {
      const assetId = params?.assetId;
      const page = params?.page;
      const limit = params?.limit;

      const basePath = `/editorial/asset/${assetId}/revisions/`;

      // Build query params provided
      const qps: Partial<GetAssetRevisionHistoryParams> = {};

      if (page) {
        qps.page = page;
      }

      if (limit) {
        qps.limit = limit;
      }

      const qpStr = qs.stringify(qps, qsOptions);
      const path = qpStr ? `${basePath}?${qpStr}` : basePath;

      const { data } = await httpClient<GetAssetRevisionHistoryResult>({
        url: path,
        method: 'GET',
      });

      return data;
    } catch (error) {
      console.error('[getAssetRevisionHistory] Error:', error);
      throw error;
    }
  },

  // Revisions Content Diff.
  getRevisionsContentDiff: async (
    params: GetRevisionsContentDiffParams,
  ): Promise<GetRevisionsContentDiffResult> => {
    try {
      const assetId = params?.assetId;
      const previousId = params?.previousId;
      const revisionId = params?.revisionId;

      // Swap previousId and revisionId to get the diff due to how the backend works.
      const path = `/editorial/asset/${assetId}/revisions/${previousId}/diff/`;
      const queryParams = qs.stringify({ against_revision_id: revisionId });
      const pathWithParams = `${path}?${queryParams}`;

      const { data, status } = await httpClient({
        url: pathWithParams,
        method: 'GET',
      });

      const result: GetRevisionsContentDiffResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getRevisionsContentDiff] Error:', error);
      throw error;
    }
  },

  // Get asset revision by id
  getAssetRevisionById: async (
    params: GetAssetRevisionByIdParams,
  ): Promise<GetAssetRevisionByIdResult> => {
    try {
      const assetId = params?.assetId;
      const revisionId = params?.revisionId;

      const path = `/editorial/asset/${assetId}/revisions/${revisionId}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      const result: GetAssetRevisionByIdResult = {
        status: data?.status || status,
        data: data?.data,
        revisionId: revisionId,
        assetId: assetId,
      };

      return result;
    } catch (error) {
      console.error('[getAssetRevisionById] Error:', error);
      throw error;
    }
  },

  getEditorialAssetById: async (
    params: GetEditorialAssetByIdArgs,
  ): Promise<GetEditorialAssetByIdResult> => {
    const assetId = params?.assetId;
    const preview = params?.preview;

    if (!assetId) {
      throw new Error('[getEditorialAssetById] Asset ID is required.');
    }

    try {
      const path = `/editorial/asset/${assetId}/`;
      const query = new URLSearchParams();

      if (preview) {
        for (const [key, value] of Object.entries(preview)) {
          query.append(`preview[${key}]`, value);
        }
      }

      const urlWithParams = query.toString() ? `${path}?${query.toString()}` : path;

      const { data, status } = await httpClient({
        url: urlWithParams,
        method: 'GET',
      });

      const result: GetEditorialAssetByIdResult = {
        status: data?.status ?? status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getEditorialAssetById] Error:', error);
      throw error;
    }
  },

  // -------------------------------
  // Patch Editorial Asset
  // -------------------------------

  patchEditorialAsset: async (
    params: PatchEditorialAssetArgs,
  ): Promise<PatchEditorialAssetResult> => {
    try {
      const assetId = params?.assetId;
      const patches = params?.patchList;

      if (!assetId) {
        throw new Error('[patchEditorialAsset] Asset ID is required.');
      }

      if (!patches || !Array.isArray(patches)) {
        throw new Error('[patchEditorialAsset] Patch data is required.');
      }

      const path = `/editorial/asset/${assetId}/`;

      // Stringify patch list array
      const patchBody = JSON.stringify(patches);

      const { data, status } = await httpClient({
        url: path,
        method: 'PATCH',
        data: patchBody,
      });

      const result: PatchEditorialAssetResult = {
        status: data?.status ?? status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[patchEditorialAsset]:', error);
      throw error;
    }
  },

  // -------------------------------
  // Video Trimming
  // -------------------------------
  postTrimmedVideo: async (
    assetId: string,
    params: TPostTrimmedVideo,
  ): Promise<TPostTrimmedVideoResponse> => {
    try {
      const { data } = await httpClient({
        url: `/editorial/asset/${assetId}/video/clip/`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(params),
      });

      return data;
    } catch (error) {
      console.error('[postTrimmedVideo] Error:', error);
      throw error;
    }
  },

  // -------------------------------
  // Editorial Asset Locking
  // -------------------------------

  // GET /editorial/asset/{id}/lock/
  getAssetIdLock: async (
    params: GetEditorialAssetIdLockParams,
  ): Promise<GetEditorialAssetIdLockResult> => {
    try {
      const assetId = params?.path?.id;

      if (!assetId) {
        throw new Error('[getAssetIdLock] Asset ID is required.');
      }

      const url = `/editorial/asset/${assetId}/lock/`;

      const { data } = await httpClient<GetEditorialAssetIdLockResult>({
        url: url,
        method: 'GET',
      });

      const result = {
        status: data?.status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getAssetIdLock] Error:', error);
      throw error;
    }
  },

  // PUT /editorial/asset/{id}/lock/
  putAssetIdLock: async (
    params: PutEditorialAssetIdLockParams,
  ): Promise<PutEditorialAssetIdLockResult> => {
    try {
      const path = params?.path;
      const body = params?.body;

      if (!path.id) {
        throw new Error('[putAssetIdLock] Asset ID is required.');
      }

      // if missing body or origin is not our const
      // then create and/or set origin to our value
      if (!body || body?.origin !== NXT_LOCK_ORIGIN) {
        params.body = {
          origin: NXT_LOCK_ORIGIN,
        };
      }

      const url = `/editorial/asset/${path?.id}/lock/`;

      const { data, status } = await httpClient({
        url: url,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(body),
      });

      // Success is 204 No Content
      const result = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[putAssetIdLock] Error:', error);
      throw error;
    }
  },

  // DELETE /editorial/asset/{id}/lock/
  deleteAssetIdLock: async (
    params: DeleteEditorialAssetIdLockParams,
  ): Promise<DeleteEditorialAssetIdLockResult> => {
    try {
      const assetId = params?.path?.id;
      const force = params?.query?.force;

      if (!assetId) {
        throw new Error('[deleteAssetIdLock] Asset ID is required.');
      }

      const queryString = force ? '?force=true' : '';
      const url = `/editorial/asset/${assetId}/lock/${queryString}`;

      const { data, status } = await httpClient({
        url: url,
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });

      // Success is 204 No Content
      const result = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[deleteAssetIdLock] Error:', error);
      throw error;
    }
  },

  // POST /editorial/asset/delete/ - delete multiple assets by uuid[]
  deleteAssetsByIds: async (
    params: PostEditorialAssetDeleteParams,
  ): Promise<PostEditorialAssetDeleteResult> => {
    try {
      if (!params || !params?.uuid) {
        throw new Error('[postAssetDelete] Asset IDs are required.');
      }

      const path = `/editorial/asset/delete/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(params),
      });

      const result = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[postAssetDelete] Error:', error);
      throw error;
    }
  },

  // DELETE /editorial/asset/{id}/ - delete a single asset by its uuid
  deleteAssetById: async (
    params: DeleteEditorialAssetIdParams,
  ): Promise<DeleteEditorialAssetIdResult> => {
    try {
      const assetId = params?.id;

      if (!assetId) {
        throw new Error('[deleteAssetById] Asset ID is required.');
      }

      const url = `/editorial/asset/${assetId}/`;

      const { data, status } = await httpClient({
        url: url,
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });

      const result = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[deleteAssetById] Error:', error);
      throw error;
    }
  },

  // DELETE /editorial/asset/{id}/ - delete a single asset by its uuid
  purgeAssetById: async (
    params: PurgeEditorialAssetByIdParams,
  ): Promise<PurgeEditorialAssetByIdResult> => {
    try {
      const assetId = params?.id;

      if (!assetId) {
        throw new Error('[purgeAssetById] Asset ID is required.');
      }

      const url = `/editorial/asset/${assetId}/purge/`;

      const { data, status } = await httpClient({
        url: url,
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });

      const result = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[purgeAssetById] Error:', error);
      throw error;
    }
  },

  // PUT /editorial/asset/{id}/restore/ - restore a previously deleted asset by uuid
  restoreAssetById: async (
    params: RestoreEditorialAssetByIdParams,
  ): Promise<RestoreEditorialAssetByIdResult> => {
    try {
      const assetId = params?.id;

      if (!assetId) {
        throw new Error('[restoreAssetById] Asset ID is required.');
      }

      const url = `/editorial/asset/${assetId}/restore/`;

      const { data, status } = await httpClient({
        url: url,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });

      const result = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[restoreAssetById] Error:', error);
      throw error;
    }
  },

  // POST /editorial/asset/{id}/duplicate/ - duplicate asset by its uuid
  duplicateAssetById: async (
    params: PostEditorialAssetDuplicateFullParams,
  ): Promise<PostEditorialAssetDuplicateResult> => {
    try {
      const assetId = params?.path?.id;

      if (!assetId) {
        throw new Error('[duplicateAssetById] Asset ID is required.');
      }

      const url = `/editorial/asset/${assetId}/duplicate/`;

      const config: AxiosRequestConfig = {
        url: url,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      };

      // If params.body is not empty, add it to the request
      if (params?.body) {
        config.data = JSON.stringify(params.body);
      }

      const { data, status } = await httpClient(config);

      const result = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[duplicateAssetById] Error:', error);
      throw error;
    }
  },

  getBlackbirdLink: async (): Promise<GetBlackbirdLinkResult> => {
    try {
      const path = `/editorial/video/vms/externaleditor/`;

      const { data } = await httpClient<GetBlackbirdLinkResult>({
        url: path,
        method: 'GET',
      });

      const result: GetBlackbirdLinkResult = {
        status: data?.status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getBlackbirdLink] Error:', error);
      throw error;
    }
  },

  // GET /editorial/config/collection/dynamic/
  getDynamicCollectionConfig: async (): Promise<GetDynamicCollectionConfigResult> => {
    try {
      const path = `/editorial/config/collection/dynamic/`;

      const { data, status } = await httpClient<GetDynamicCollectionConfigResult>({
        url: path,
        method: 'GET',
      });

      const result: GetDynamicCollectionConfigResult = {
        status: data?.status ?? status,
        data: data?.data,
      };
      return result;
    } catch (error) {
      console.error('[getDynamicCollectionConfig]:', error);
      throw error;
    }
  },

  // POST /editorial/asset/search/collection/dynamic/
  getDynamicCollectionSearch: async (
    params: GetDynamicCollectionSearchArgs,
  ): Promise<GetDynamicCollectionSearchResult> => {
    try {
      const path = `/editorial/asset/search/collection/dynamic/`;
      const signal = params?.signal;
      const body = params?.body || {};

      const { data, status } = await httpClient<GetDynamicCollectionSearchResult>({
        url: path,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(body),
        signal: signal,
      });

      const result: GetDynamicCollectionSearchResult = {
        status: data?.status ?? status,
        data: data?.data,
      };
      return result;
    } catch (error) {
      console.error('[dynamicCollectionSearch]:', error);
      throw error;
    }
  },
};

// Unload delete (hail mary unlock) for asset lock using
// axios fetch adapter for keepalive support.
// In all other cases if not hard navigating away,
// prefer using: `deleteAssetIdLock` above...
// NOTE: using base js fetch does not refresh the
// auth token if needed - only axios httpClient handles token refresh...

type DeleteAssetLockOptions = {
  assetId: string;
  force?: boolean;
  fallbackFn?: (id: string) => Promise<void>;
};

export async function deleteAssetIdLockKeepalive(
  options: DeleteAssetLockOptions,
): Promise<void> {
  const assetId = options?.assetId;
  const force = options?.force === true;
  const fallbackFn = options?.fallbackFn;

  if (!assetId) {
    console.warn('[deleteAssetIdLockKeepalive] Asset ID is required.');
    return;
  }

  const query = force ? '?force=true' : '';
  const path = `/editorial/asset/${assetId}/lock/${query}`;

  try {
    await httpClient({
      url: path,
      method: 'DELETE',
      adapter: 'fetch', // keepalive requires fetch
      fetchOptions: {
        keepalive: true,
        priority: 'high',
      },
    });
  } catch (error) {
    console.warn('[deleteAssetIdLockKeepalive] keepalive failed', error);
    if (fallbackFn) {
      try {
        await fallbackFn(assetId);
      } catch (fallbackErr) {
        console.error('[deleteAssetIdLockKeepalive] fallback failed', fallbackErr);
      }
    }
  }
}

/**
 * Types for specific Editorial functions
 *
 * Some of these, eg search params/args may
 * not be 1-to-1 with the RA getList params.
 * These have stricter types for more direct
 * programmatic usage (eg: AI tools, etc...)
 */

export type BaseEditorialSearchArgs = {
  qp?: BaseEditorialSearchParams;
  signal?: TAbortCtrlSignal;
};

export type BaseEditorialSearchResponse = {
  status?: string | number;
  data?: {
    next?: string | number | null;
    prev?: string | number | null;
    total?: number | null;
    items?: EditorialAssetSearchResultItem[];
    facets?: EditorialSearchFacets;
  };
};

// Params for base editorial search api function
// Note: may not be 1-to-1 with the RA getList params
export type BaseEditorialSearchParams = {
  /** search query text */
  query?: string;
  /** (experimental) Semantic search query text. */
  semantic?: string;
  /** (experimental) Number of nearest neighbors to retrieve for semantic search. */
  semantic_nearest?: number;
  /** (experimental) Number of candidates to consider for semantic search. */
  semantic_candidates?: number;
  /** Author UUID. */
  author?: string;
  /** Asset type, subtype */
  type?: string[];
  subtype?: string[];
  /** Flags */
  flag?: string[];
  /**  Limit to or exclude contributed assets. */
  contributed?: boolean;
  /**  Limit to or exclude advertorial assets */
  advertorial?: boolean;
  /** Sections */
  section?: string[];
  /** slug */
  print_slug?: string;
  /** 'published' | 'unpublished' | 'awaiting_processing' */
  publish_status?: string[];
  /** start time range - relative-date-time */
  start_time?: {
    min?: string;
    max?: string;
  };
  /** show deleted assets */
  deleted?: boolean;
  print_ready?: 'yes' | 'no' | string;
  photo_sales?: 'yes' | 'no' | string;
  /** source application */
  app?: string[];
  /** workflow ids */
  workflow?: string[];
  /** workflow process ids */
  workflow_process?: string[];
  /** print depth range */
  print_depth?: {
    min?: number;
    max?: number;
  };
  /** print publication code */
  print_publication?: string;
  /** print publication range */
  print_publication_date?: {
    min?: string;
    max?: string;
  };
  print_publication_status?: 'placed' | 'assigned' | string;
  /** search template assets instead of content assets */
  template?: 'yes' | 'no' | string;
  /** Limit to draft revisions */
  draft?: 'yes' | 'no' | string;
  /** Asset UUIDs to include in results */
  uuid?: string[];
  /** include previews request */
  preview?: PreviewRequest;
  /** Pagination and sorting parameters */
  page?: string | number;
  limit?: number;
  /** field to sort by */
  sort?: string;
  dir?: 'asc' | 'desc' | 'ASC' | 'DESC' | string;
};

/**
 * Editorial Search Facets
 */
export type EditorialSearchFacets = {
  type?: {
    article?: number;
    image?: number;
    collection?: number;
    audio?: number;
    file?: number;
    flash?: number;
    html?: number;
    link?: number;
    pdf?: number;
    poll?: number;
    table?: number;
    video?: number;
    youtube?: number;
    zip?: number;
    [key: string]: number | undefined;
  };
  section?: {
    [key: string]: number | undefined;
  };
  flag?: {
    [key: string]: number | undefined;
  };
  publish_status?: {
    published?: number;
    unpublished?: number;
    awaiting_processing?: number;
    [key: string]: number | undefined;
  };
  app?: {
    editorial?: number;
    [key: string]: number | undefined;
  };
  workflow?: {
    [key: string]: number | undefined;
  };
  workflow_process?: {
    [key: string]: number | undefined;
  };
  print_ready?: {
    yes?: number;
    no?: number;
    [key: string]: number | undefined;
  };
  // allow any other facet groups the API might return
  [key: string]: any;
};
